import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageListContainer, PageCreateButton } from "../../../components/Page";
import ProjectDialog from "../../../components/GenericDialog";
import { useConfirm } from "../../../components/feedback";
import authService from "../../../services/authService";
import { customerService, type Customer } from "../../../services/customerService";
import { sourceSystemService } from "../../../services/sourceSystemService";
import { resolvePreferredUsername, resolveProfilesAccess } from "../../../utils/authBootstrap";
import { extractRolesFromUser } from "../../../utils/authorization";
import SourceSystemModel from "../../source-systems/model";
import ProfileSourceSystemModel, { getProfileSourceSystemCols } from "../model";
import CustomerDetailsReadOnly from "./components/CustomerDetailsReadOnly";
import CustomerLocalizationEditor from "./components/CustomerLocalizationEditor";
import CustomerWorkHoursEditor from "./components/CustomerWorkHoursEditor";
import ProfileSourceSystemDialogBody from "./components/ProfileSourceSystemDialogBody";
import ProfileSourceSystemsTable from "./components/ProfileSourceSystemsTable";

const showSnack = (opts: unknown) => {
  window.dispatchEvent(new CustomEvent("showGlobalNotification", { detail: opts }));
};

const mapSourceSystemForm = (
  formJson: Record<string, unknown>,
  customerId: string,
) => ({
  name: String(formJson.sourceName || ""),
  logo: String(formJson.sourceLogo || ""),
  contacts: formJson.contacts ? JSON.parse(String(formJson.contacts)) : [],
  sources: formJson.sources ? JSON.parse(String(formJson.sources)) : [],
  status: (formJson.sourceStatus || "ACTIVE") as "ACTIVE" | "PASSIVE",
  customerId,
});

function ProfilesPage() {
  const { t } = useTranslation();
  const cols = useMemo(() => getProfileSourceSystemCols(t), [t]);
  const datatable = useRef<any>(null);
  const createSourceSystemDialogRef = useRef<any>(null);
  const editSourceSystemDialogRef = useRef<any>(null);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [canManageSourceSystems, setCanManageSourceSystems] = useState(false);

  const loadCustomer = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const user = await authService.getUser();
      const roles = extractRolesFromUser(user);
      const profilesAccess = resolveProfilesAccess(user, roles);
      setCanManageSourceSystems(profilesAccess.canManageSourceSystems);

      const username = resolvePreferredUsername(user);
      const nextCustomer = await customerService.getMyCustomer(username);

      if (!nextCustomer?.id) {
        setCustomer(null);
        setError(t("profiles.noLinkedCustomer"));
        return;
      }

      setCustomer(nextCustomer);
    } catch (loadError) {
      console.error("Failed to load profile customer:", loadError);
      setCustomer(null);
      setError(t("profiles.unableToLoad"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCustomer();
  }, [loadCustomer]);

  useEffect(() => {
    const handleGlobalRefresh = () => {
      datatable.current?.refresh?.();
    };

    window.addEventListener("refreshDataTable", handleGlobalRefresh);
    return () => window.removeEventListener("refreshDataTable", handleGlobalRefresh);
  }, []);

  const openCreateSourceSystemDialog = () => {
    if (!customer?.id || !createSourceSystemDialogRef.current) {
      return;
    }

    createSourceSystemDialogRef.current.showDialog(ProfileSourceSystemModel.createDialogOpts(t), {
      customerId: customer.id,
    });
  };

  const createSourceSystemSubmitAction = (formJson: Record<string, unknown>) => {
    if (!customer?.id) {
      return;
    }

    sourceSystemService
      .createSourceSystem(mapSourceSystemForm(formJson, customer.id))
      .then(() =>
        SourceSystemModel.message(
          SourceSystemModel.SUCCESS,
          t("common.createRequest"),
          t("profiles.sourceCreated"),
          Date.now(),
        ),
      )
      .then((opts) => {
        showSnack(opts);
        setTimeout(() => datatable.current?.refresh?.(), 500);
      })
      .catch((submitError: unknown) => {
        console.error(submitError);
      });
  };

  const editSourceSystemSubmitAction = (formJson: Record<string, unknown>) => {
    if (!customer?.id) {
      return;
    }

    const mapped = mapSourceSystemForm(formJson, customer.id);

    sourceSystemService
      .updateSourceSystem({
        id: String(formJson.id || ""),
        ...mapped,
      })
      .then(() =>
        SourceSystemModel.message(
          SourceSystemModel.SUCCESS,
          t("common.updateRequest"),
          t("profiles.sourceUpdated"),
          Date.now(),
        ),
      )
      .then((opts) => {
        showSnack(opts);
        setTimeout(() => datatable.current?.refresh?.(), 500);
      })
      .catch((submitError: unknown) => {
        console.error(submitError);
      });
  };

  const handleDelete = async (item: { id?: string; name?: string }) => {
    return new Promise((resolve) => {
      confirm({
        description: t("profiles.deleteSourceConfirm", { name: item.name || item.id }),
      })
        .then(() => sourceSystemService.deleteSourceSystem(String(item.id)))
        .then(() => resolve({}))
        .then(() =>
          SourceSystemModel.message(
            SourceSystemModel.SUCCESS,
            t("common.deleteRequest"),
            t("profiles.sourceDeleted"),
            Date.now(),
          ),
        )
        .then((opts) => {
          showSnack(opts);
          setTimeout(() => datatable.current?.refresh?.(), 500);
        })
        .catch((deleteError: unknown) => {
          console.error(deleteError);
        });
    });
  };

  const handleEdit = async (item: Record<string, unknown>) => {
    if (!editSourceSystemDialogRef.current || !customer?.id) {
      return;
    }

    editSourceSystemDialogRef.current.showDialog(ProfileSourceSystemModel.editDialogOpts(t), {
      ...item,
      customerId: customer.id,
    });
  };

  return (
    <PageListContainer>
      {canManageSourceSystems && (
        <>
          <ProjectDialog
            ref={createSourceSystemDialogRef}
            submitAction={createSourceSystemSubmitAction}
            body={ProfileSourceSystemDialogBody}
            maxWidth="md"
            fullWidth
          />
          <ProjectDialog
            ref={editSourceSystemDialogRef}
            submitAction={editSourceSystemSubmitAction}
            body={ProfileSourceSystemDialogBody}
            maxWidth="md"
            fullWidth
          />
        </>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Alert severity="warning">{error}</Alert>
      ) : customer ? (
        <>
          <Box sx={{ flexShrink: 0 }}>
            <CustomerDetailsReadOnly customer={customer} />
            <CustomerWorkHoursEditor
              customer={customer}
              onUpdated={(updated) => setCustomer(updated)}
            />
            <CustomerLocalizationEditor
              customer={customer}
              onUpdated={(updated) => setCustomer(updated)}
            />
            <Typography variant="h6" color="text.primary" sx={{ mt: 3, mb: 1.5 }}>
              {t("profiles.sourceSystems")}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: 280, display: "flex", flexDirection: "column" }}>
            <ProfileSourceSystemsTable
              ref={datatable}
              customerId={String(customer.id)}
              cols={cols}
              handleEdit={canManageSourceSystems ? handleEdit : undefined}
              handleDelete={
                canManageSourceSystems
                  ? (row) => {
                      void handleDelete(row as { id?: string; name?: string });
                    }
                  : undefined
              }
              headerActions={
                canManageSourceSystems ? (
                  <PageCreateButton onClick={openCreateSourceSystemDialog}>
                    {t("profiles.newSourceSystem")}
                  </PageCreateButton>
                ) : undefined
              }
            />
          </Box>
        </>
      ) : null}
    </PageListContainer>
  );
}

export default ProfilesPage;
